// Record / correct a patient's Deceased status (Communication spec §5.5).
//
// One dialog, two modes, picked from the patient's current status:
//   * not deceased → "Record as deceased": date/time of death + a mandatory
//     reason/source.
//   * deceased     → "Correct deceased status": a mandatory reason only. This
//     reverses a status recorded in error; it is audited with the values it
//     clears, and never deletes the patient record.
// Both modes show the full record/correct history from audit_log underneath.
//
// Only Admin/Doctor are offered the dialog (see PatientRecords.tsx); the
// backend enforces the same rule on its own (routes/patient-deceased.js), so
// hiding the button is courtesy, not the gate.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input, Textarea } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { LoadingState, ErrorState } from '@/components/ui/States';
import { ApiError } from '@/lib/api/client';
import { keys } from '@/lib/queryKeys';
import { toast } from '@/hooks/useToast';
import { getDeceasedStatus, recordDeceased, correctDeceased } from './api';
import type { DeceasedHistoryEntry } from './types';

export interface DeceasedStatusDialogProps {
  patient: { uhid: string; name: string; deceased?: boolean | null };
  onClose: () => void;
  /** Called after a successful record/correct, so the caller can refetch the patient. */
  onChanged: () => void;
}

const fmtDateTime = (v: string | null | undefined) =>
  v ? new Date(v).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

// "now" as a datetime-local value in the browser's own timezone — the input
// has no zone, so toISOString() (UTC) would pre-fill the wrong wall-clock time.
function localNowInputValue() {
  const d = new Date();
  d.setSeconds(0, 0);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function HistoryList({ entries }: { entries: DeceasedHistoryEntry[] }) {
  if (!entries.length) return <div className="text-xs text-ink-muted">No changes recorded yet.</div>;
  return (
    <ol className="flex flex-col gap-2">
      {entries.map((h) => (
        <li key={h.id} className="rounded-[var(--radius)] border border-line p-2.5 text-xs">
          <div className="font-semibold">
            {h.details.event === 'deceased_recorded' ? 'Recorded as deceased' : 'Deceased status corrected (reversed)'}
          </div>
          <div className="text-ink-muted">
            {fmtDateTime(h.changed_at)} · by {h.changed_by}
          </div>
          {h.details.event === 'deceased_recorded' && h.details.deceased_at && (
            <div className="mt-1">Date of death: {fmtDateTime(h.details.deceased_at)}</div>
          )}
          {h.details.event === 'deceased_corrected' && h.details.previous?.deceased_at && (
            <div className="mt-1 text-ink-muted">
              Cleared: date of death {fmtDateTime(h.details.previous.deceased_at)}
              {h.details.previous.deceased_recorded_by ? `, recorded by ${h.details.previous.deceased_recorded_by}` : ''}
            </div>
          )}
          <div className="mt-1">Reason: {h.details.reason}</div>
        </li>
      ))}
    </ol>
  );
}

export function DeceasedStatusDialog({ patient, onClose, onChanged }: DeceasedStatusDialogProps) {
  const queryClient = useQueryClient();
  const status = useQuery({
    queryKey: keys.patients.deceased(patient.uhid),
    queryFn: () => getDeceasedStatus(patient.uhid),
  });
  // The server's answer wins over the (possibly stale) prop once it arrives.
  const isDeceased = status.data ? status.data.deceased : !!patient.deceased;

  const [deceasedAtLocal, setDeceasedAtLocal] = useState(localNowInputValue);
  const [reason, setReason] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const mutation = useMutation({
    mutationFn: () => {
      if (isDeceased) return correctDeceased(patient.uhid, reason.trim());
      // datetime-local is zoneless; new Date() reads it in the browser's
      // timezone, and toISOString() sends the server an unambiguous instant.
      const when = new Date(deceasedAtLocal);
      return recordDeceased(patient.uhid, { deceasedAt: isNaN(when.getTime()) ? '' : when.toISOString(), reason: reason.trim() });
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: keys.patients.deceased(patient.uhid) });
      const cancelled = 'cancelled_appointments' in result ? result.cancelled_appointments : 0;
      toast({
        variant: 'success',
        title: isDeceased ? 'Deceased status corrected' : 'Recorded as deceased',
        description: isDeceased
          ? 'Previously cancelled appointments are not restored — rebook them if needed.'
          : `Automated reminders and routine messages are now suppressed.${cancelled ? ` ${cancelled} booked appointment${cancelled === 1 ? ' was' : 's were'} cancelled.` : ''}`,
      });
      onChanged();
    },
    onError: (e) => setFieldErrors(e instanceof ApiError ? e.fieldErrors : {}),
  });

  const submit = () => {
    const errs: Record<string, string> = {};
    if (!reason.trim()) errs.reason = isDeceased ? 'A reason for the correction is required' : 'A reason or source is required';
    if (!isDeceased && !deceasedAtLocal) errs.deceased_at = 'Date and time of death is required';
    setFieldErrors(errs);
    if (Object.keys(errs).length) return;
    mutation.mutate();
  };

  const serverError = mutation.error instanceof ApiError && !Object.keys(mutation.error.fieldErrors).length
    ? mutation.error.message
    : mutation.error && !(mutation.error instanceof ApiError) ? 'Could not save — please try again.' : '';

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-[560px]">
        <DialogHeader>
          <DialogTitle>{isDeceased ? 'Correct deceased status' : 'Record as deceased'}</DialogTitle>
          <DialogDescription>
            {patient.name} ({patient.uhid})
          </DialogDescription>
        </DialogHeader>

        {status.isLoading ? (
          <LoadingState label="Loading status…" />
        ) : status.isError ? (
          <ErrorState
            message={status.error instanceof ApiError ? status.error.message : 'Could not load the deceased status'}
            onRetry={() => status.refetch()}
          />
        ) : (
          <>
            {isDeceased ? (
              <>
                <div className="rounded-[var(--radius)] bg-muted p-3 text-[13px]">
                  <div>Date of death: <strong>{fmtDateTime(status.data?.deceased_at)}</strong></div>
                  <div className="text-xs text-ink-muted">
                    Recorded by {status.data?.deceased_recorded_by ?? '—'} on {fmtDateTime(status.data?.deceased_recorded_at)}
                  </div>
                  {status.data?.deceased_reason && <div className="mt-1 text-xs">Reason: {status.data.deceased_reason}</div>}
                </div>
                <div className="text-[13px] leading-relaxed text-ink-muted">
                  Use this only if the patient was recorded as deceased in error. The correction is audited with your name and
                  reason, and the previous entry stays in the history below. Appointments that were cancelled when the death
                  was recorded are not restored.
                </div>
              </>
            ) : (
              <>
                <div className="text-[13px] leading-relaxed text-ink-muted">
                  This marks the patient record as deceased. The record is kept, not deleted. Their booked appointments are
                  cancelled, no new appointments can be booked, and automated reminders and other routine messages stop.
                </div>
                <FormField label="Date and time of death" required error={fieldErrors.deceased_at} htmlFor="deceased-at">
                  <Input
                    id="deceased-at"
                    type="datetime-local"
                    value={deceasedAtLocal}
                    max={localNowInputValue()}
                    invalid={!!fieldErrors.deceased_at}
                    onChange={(e) => setDeceasedAtLocal(e.target.value)}
                  />
                </FormField>
              </>
            )}

            <FormField
              label={isDeceased ? 'Reason for correction' : 'Reason / source'}
              required
              error={fieldErrors.reason}
              htmlFor="deceased-reason"
            >
              <Textarea
                id="deceased-reason"
                className="h-[70px]"
                value={reason}
                invalid={!!fieldErrors.reason}
                maxLength={1000}
                onChange={(e) => setReason(e.target.value)}
                placeholder={isDeceased
                  ? 'e.g. Recorded against the wrong patient — confirmed alive at OP visit today'
                  : 'e.g. Death certificate no. …, died during admission, informed by family'}
              />
            </FormField>

            {serverError && (
              <div className="rounded-[var(--radius)] bg-danger-soft px-3 py-2 text-xs text-danger-ink">{serverError}</div>
            )}

            <DialogFooter>
              <Button variant="ghost" onClick={onClose}>Cancel</Button>
              <Button variant={isDeceased ? 'primary' : 'danger'} disabled={mutation.isPending} onClick={submit}>
                {mutation.isPending ? 'Saving…' : isDeceased ? 'Save correction' : 'Record as deceased'}
              </Button>
            </DialogFooter>

            <div>
              <div className="lbl">History</div>
              <HistoryList entries={status.data?.history ?? []} />
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
